import { useState } from "react";
import { AnimatePresence, motion } from "framer-motion";
import { Plus } from "lucide-react";
import { Reveal, SectionHead } from "./Reveal";

const faqs = [
  {
    q: "How many sessions will I need?",
    a: "The package includes 10 sessions. The exact response varies between individuals because hair-growth cycles, hair thickness, hormones and skin type differ.",
  },
  {
    q: "Is laser hair removal permanent?",
    a: "Laser treatment provides long-term hair reduction rather than a guarantee of permanent complete hair removal. Some maintenance may be required.",
  },
  {
    q: "Is underarm laser treatment painful?",
    a: "Most patients tolerate it well, but sensation varies. You may feel warmth, snapping or mild discomfort during treatment.",
  },
  {
    q: "Can all skin types undergo laser hair reduction?",
    a: "Many skin types can be treated with appropriate laser technology and settings, but suitability should be assessed individually.",
  },
  {
    q: "How often are sessions done?",
    a: "The interval is decided based on your hair-growth cycle and clinical assessment.",
  },
  {
    q: "What if I have hormonal hair growth?",
    a: "Hormonal factors can influence hair growth and treatment response. Your doctor can assess whether additional medical evaluation is appropriate.",
  },
  {
    q: "Does the ₹22,500 package include maintenance?",
    a: "Yes. The package includes one complimentary maintenance session every year for up to 10 years, subject to the clinic's package terms and clinical suitability.",
  },
  {
    q: "Is EMI available?",
    a: "No-cost EMI options may be available for eligible patients. Contact the clinic for current terms.",
  },
];

const FaqItem = ({ q, a, index }) => {
  const [open, setOpen] = useState(index === 0);
  return (
    <div data-testid={`faq-item-${index + 1}`} className="border-b border-champagne/60">
      <button
        onClick={() => setOpen((o) => !o)}
        data-testid={`faq-question-${index + 1}`}
        aria-expanded={open}
        className="flex w-full items-center justify-between gap-6 py-6 text-left group"
      >
        <span className="font-display text-lg md:text-2xl font-light tracking-tight text-ink group-hover:text-teal transition-colors duration-300">
          {q}
        </span>
        <motion.span
          animate={{ rotate: open ? 45 : 0 }}
          transition={{ duration: 0.35, ease: [0.22, 1, 0.36, 1] }}
          className="flex h-9 w-9 shrink-0 items-center justify-center rounded-full border border-champagne text-teal"
        >
          <Plus className="w-4 h-4" strokeWidth={1.6} />
        </motion.span>
      </button>
      <AnimatePresence initial={false}>
        {open && (
          <motion.div
            initial={{ height: 0, opacity: 0 }}
            animate={{ height: "auto", opacity: 1 }}
            exit={{ height: 0, opacity: 0 }}
            transition={{ duration: 0.45, ease: [0.22, 1, 0.36, 1] }}
            className="overflow-hidden"
          >
            <p data-testid={`faq-answer-${index + 1}`} className="pb-7 pr-14 text-sm md:text-base font-light leading-relaxed text-ink/60">
              {a}
            </p>
          </motion.div>
        )}
      </AnimatePresence>
    </div>
  );
};

export const FAQ = () => (
  <section id="faq" data-testid="faq-section" className="py-24 md:py-36">
    <div className="max-w-4xl mx-auto px-5 md:px-10">
      <SectionHead index="10" eyebrow="Questions, Answered" title="Frequently Asked Questions" align="center" />
      <Reveal delay={0.15} className="mt-14">
        <div className="border-t border-champagne/60">
          {faqs.map((f, i) => (
            <FaqItem key={f.q} q={f.q} a={f.a} index={i} />
          ))}
        </div>
      </Reveal>
    </div>
  </section>
);
